// sales-page/src/pages/PartnerPitch.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import salesApi from '../core/salesApi';
import salesLifetimeCss from './SalesLifetime.css?raw';
import { buildPartnerUpsellHtml } from './partnerUpsellMarkup';
import { legalContent } from './legalContent';

const PartnerPitch = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const returnToken = searchParams.get('token'); // drip email returning visitor
  const upsellContainerRef = useRef(null);
  const [legalModal, setLegalModal] = useState(null); // key into legalContent, or null

  // Page states: 'verifying' | 'loading_slots' | 'full' | 'ready' | 'error'
  const [pageState, setPageState] = useState('verifying');
  const [sessionData, setSessionData] = useState(null);   // { user_id, customer_id, first_name, email, plan_name, plan_amount }
  const [slotsRemaining, setSlotsRemaining] = useState(null);
  const [chargeState, setChargeState] = useState('idle'); // 'idle' | 'charging' | 'success' | 'error'
  const [chargeError, setChargeError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  // Page-scoped CSS, same design system as the main sales page -- injected on
  // mount, removed on unmount so it never leaks onto /checkout etc.
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-partner-upsell', 'true');
    styleEl.textContent = salesLifetimeCss;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  useEffect(() => {
    initializePage();
  }, []);

  // Wire the static markup's CTA buttons + decline link to the real handlers.
  // Re-runs whenever the markup's dynamic values change (dangerouslySetInnerHTML
  // replaces the subtree each time, so listeners need reattaching).
  useEffect(() => {
    const container = upsellContainerRef.current;
    if (!container) return undefined;

    const ctaEls = container.querySelectorAll('.founder-cta-btn');
    const onCtaClick = (e) => {
      e.preventDefault();
      setShowConfirm(true);
    };
    ctaEls.forEach((el) => el.addEventListener('click', onCtaClick));

    const declineEl = container.querySelector('#decline-link');
    const onDeclineClick = (e) => {
      e.preventDefault();
      handleSkip();
    };
    if (declineEl) declineEl.addEventListener('click', onDeclineClick);

    const legalEls = container.querySelectorAll('[data-legal]');
    const onLegalClick = (e) => {
      e.preventDefault();
      setLegalModal(e.currentTarget.getAttribute('data-legal'));
    };
    legalEls.forEach((el) => el.addEventListener('click', onLegalClick));

    return () => {
      ctaEls.forEach((el) => el.removeEventListener('click', onCtaClick));
      if (declineEl) declineEl.removeEventListener('click', onDeclineClick);
      legalEls.forEach((el) => el.removeEventListener('click', onLegalClick));
    };
  }, [pageState, slotsRemaining, sessionData]);

  const initializePage = async () => {
    // Must have either session_id (post-checkout) or token (drip email return)
    if (!sessionId && !returnToken) {
      navigate('/');
      return;
    }

    try {
      // Step 1: verify session or token and get user/customer data
      const verification = await salesApi.verifyPitchAccess({
        session_id: sessionId || null,
        token: returnToken || null
      });

      if (!verification.success) {
        setErrorMessage(verification.message || 'Unable to verify your session. Please contact support.');
        setPageState('error');
        return;
      }

      setSessionData(verification.data);
      setPageState('loading_slots');

      // Step 2: check partner slots
      const slotData = await salesApi.getPartnerCount();
      const remaining = slotData.data?.spots_remaining ?? 0;
      const isFull = slotData.data?.is_full ?? true;

      // First batch is 50 spots only. Backend tracks 100 total.
      // Cap at 50 max, then subtract 1 reserved spot.
      const firstBatchRemaining = Math.min(remaining, 50);
      setSlotsRemaining(Math.max(0, firstBatchRemaining - 1));

      // Close upsell when first batch (50) is full
      if (firstBatchRemaining <= 1) {
        setPageState('full');
        return;
      }

      setPageState('ready');
    } catch (err) {
      console.error('PartnerPitch init error:', err);
      setErrorMessage('Something went wrong loading this page. Please contact support.');
      setPageState('error');
    }
  };

  const handleAccept = async () => {
    if (chargeState === 'charging') return;
    setChargeState('charging');
    setChargeError('');

    try {
      const result = await salesApi.chargeBadgeUpsell({
        customer_id: sessionData.customer_id,
        user_id: sessionData.user_id,
        email: sessionData.email,
        session_id: sessionId || null,
        token: returnToken || null
      });

      if (!result.success) {
        setChargeError(result.message || 'Payment failed. Please try again or contact support.');
        setChargeState('error');
        return;
      }

      // ── Fallback path: card was detached, backend created a fresh Checkout
      //    session — redirect the user to Stripe to re-enter their card. ──────
      if (result.requires_redirect && result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }

      setChargeState('success');

      // Clear email verification from localStorage since purchase is complete
      salesApi.clearEmailVerification();

      // After brief success moment navigate to thank you badge
      setTimeout(() => {
        navigate('/thank-you/badge');
      }, 2000);
    } catch (err) {
      console.error('Badge charge error:', err);
      setChargeError(
        err.response?.data?.message || 'Payment failed. Please check your card details or contact support.'
      );
      setChargeState('error');
    }
  };

  const handleSkip = async () => {
    // Fire skip to backend so it can set badge_status: declined and queue drip
    try {
      await salesApi.skipBadgeUpsell({
        user_id: sessionData?.user_id || null,
        session_id: sessionId || null,
        token: returnToken || null
      });
    } catch (err) {
      // Non-blocking — don't stop navigation if this fails
      console.error('Skip badge upsell notify error:', err);
    }

    salesApi.clearEmailVerification();
    navigate('/thank-you/plan');
  };

  // ─── RENDER: VERIFYING ───────────────────────────────────────────────────────
  if (pageState === 'verifying' || pageState === 'loading_slots') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pampa-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-xl text-gray-700 font-medium">
            {pageState === 'verifying' ? 'Confirming your payment...' : 'Loading exclusive offer...'}
          </p>
        </div>
      </div>
    );
  }

  // ─── RENDER: ERROR ───────────────────────────────────────────────────────────
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Something Went Wrong</h1>
          <p className="text-lg text-gray-600 mb-8">{errorMessage}</p>
          <button
            onClick={() => navigate('/thank-you/plan')}
            className="px-8 py-3 bg-pampa-600 text-white rounded-xl font-semibold hover:bg-pampa-700 transition-all"
          >
            Go to My Account
          </button>
        </div>
      </div>
    );
  }

  // ─── RENDER: SLOTS FULL ──────────────────────────────────────────────────────
  if (pageState === 'full') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-2xl text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">All 50 Founding Partner Spots Are Taken</h1>
          <p className="text-xl text-gray-600 mb-4">
            The first batch of 50 founding partner spots has closed.
          </p>
          <p className="text-lg text-gray-600 mb-4">
            Your platform plan is fully active and everything that comes with it is yours. You made a great decision buying the platform and nothing about that changes.
          </p>
          <p className="text-base text-gray-500 mb-8">
            When TrafikTrak reaches <strong>$1,000,000 in net revenue</strong>, the remaining 50 spots will re-open — at <strong>$10,000 each</strong>. The first batch was priced at $197 because you got in before the proof existed. The second batch price reflects what the position is worth once the platform has a demonstrated track record.
          </p>
          <p className="text-sm text-gray-400 mb-8">
            If you want to be notified when the second batch opens, email us at support@trafiktrak.com.
          </p>
          <button
            onClick={() => {
              salesApi.clearEmailVerification();
              navigate('/thank-you/plan');
            }}
            className="px-12 py-4 bg-pampa-600 text-white rounded-xl font-bold text-lg hover:bg-pampa-700 transition-all shadow-lg"
          >
            Access My Account →
          </button>
        </div>
      </div>
    );
  }

  // ─── RENDER: CHARGE SUCCESS ──────────────────────────────────────────────────
  if (chargeState === 'success') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-xl text-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Welcome To The Partnership!</h1>
          <p className="text-xl text-gray-600 mb-2">Your Partnership Badge is confirmed.</p>
          <p className="text-lg text-gray-500">Sending your access code now...</p>
        </div>
      </div>
    );
  }

  // ─── RENDER: MAIN PITCH PAGE ─────────────────────────────────────────────────
  const firstName = sessionData?.first_name || '';
  const planName = sessionData?.plan_name || 'your plan';
  const planAmount = sessionData?.plan_amount || '';
  const isReturningVisitor = !!returnToken;

  const upsellHtml = buildPartnerUpsellHtml({
    slotsRemaining,
    planName,
    planAmount,
    firstName,
    isReturningVisitor
  });

  return (
    <div className="min-h-screen font-['Arial',_sans-serif]">

      {/* eslint-disable-next-line react/no-danger */}
      <div ref={upsellContainerRef} dangerouslySetInnerHTML={{ __html: upsellHtml }} />

      {/* ── CONFIRMATION MODAL -- same "Google Doc" bordered-box aesthetic as
           the rest of the page (sharp black border, red CTA, blue price,
           green checklist rows) instead of the generic rounded Tailwind card. ── */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div
            className="max-w-md w-full relative"
            style={{ background: '#FFFFFF', border: '1.5px solid #111111', borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: '28px' }}
          >
            <h3 style={{ fontSize: '24px', fontWeight: 900, color: '#111111', textAlign: 'center', marginBottom: '18px', lineHeight: 1.25 }}>
              Confirm Your Founding Partner Position
            </h3>

            <div style={{ border: '1.5px solid #111111', borderRadius: '4px', padding: '16px 18px', marginBottom: '18px' }}>
              <div className="checklist-item">
                <span className="check-icon">&#9989;</span>
                <div>Founding Partner Position &mdash; <strong>50% commission, permanent</strong></div>
              </div>
              <div className="checklist-item" style={{ marginBottom: 0 }}>
                <span className="check-icon">&#9989;</span>
                <div>
                  Charged to your saved card:{' '}
                  <span style={{ color: '#1D4ED8', fontWeight: 800, fontSize: '20px' }}>$197</span>
                </div>
              </div>
            </div>

            <p style={{ fontSize: '14px', color: '#434343', textAlign: 'center', marginBottom: '18px', fontStyle: 'italic' }}>
              This is a one-time charge to the same card you used at checkout. No re-entry needed.
            </p>

            {chargeError && (
              <div style={{ border: '1.5px solid #DC2626', background: '#FEF2F2', borderRadius: '4px', padding: '10px 14px', marginBottom: '14px' }}>
                <p style={{ fontSize: '14px', color: '#DC2626', fontWeight: 700 }}>{chargeError}</p>
              </div>
            )}

            <a
              href="#offer"
              onClick={(e) => { e.preventDefault(); handleAccept(); }}
              className="doc-cta-button"
              style={{ opacity: chargeState === 'charging' ? 0.6 : 1, pointerEvents: chargeState === 'charging' ? 'none' : 'auto', fontSize: '19px' }}
            >
              {chargeState === 'charging' ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing Payment...
                </span>
              ) : (
                <>&lt; &#9989; YES &mdash; CHARGE $197 AND CONFIRM &gt;</>
              )}
            </a>

            <button
              onClick={() => { setShowConfirm(false); setChargeError(''); }}
              disabled={chargeState === 'charging'}
              style={{ width: '100%', marginTop: '10px', padding: '10px', background: 'none', border: 'none', color: '#666666', textDecoration: 'underline', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Go back &mdash; I want to keep reading
            </button>
          </div>
        </div>
      )}

      {/* ── LEGAL/POLICY MODAL ── */}
      {legalModal && legalContent[legalModal] && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={() => setLegalModal(null)}>
          <div
            className="max-w-2xl w-full relative"
            style={{ background: '#FFFFFF', border: '1.5px solid #111111', borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: '32px', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLegalModal(null)}
              style={{ position: 'absolute', top: '18px', right: '18px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666666', lineHeight: 1 }}
              aria-label="Close"
            >
              &times;
            </button>
            <h3 style={{ fontSize: '26px', fontWeight: 900, color: '#111111', marginBottom: '18px' }}>
              {legalContent[legalModal].title}
            </h3>
            <div
              style={{ fontSize: '16px', lineHeight: 1.65, color: '#333333' }}
              dangerouslySetInnerHTML={{ __html: legalContent[legalModal].body }}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default PartnerPitch;